import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { resolveClinicForUser } from '../lib/clinicResolver';
import { useWarehouse } from '../contexts/WarehouseContext';
import RemoveItemsDialog from './RemoveItemsDialog';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const NewMedicalRecordPage = () => {
  const navigate = useNavigate();
  const { petId } = useParams();
  const [user, setUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [pets, setPets] = useState([]);
  
  const [currentVet, setCurrentVet] = useState(null);
  
  // Warehouse integration
  const { items: warehouseItems, removeItems } = useWarehouse();
  const [showWarehouseDialog, setShowWarehouseDialog] = useState(false);
  const [pendingWarehouseChanges, setPendingWarehouseChanges] = useState([]);
  
  const [medicalRecordForm, setMedicalRecordForm] = useState({
    pet_id: petId || '',
    client_id: '',
    date: new Date().toISOString().split('T')[0],
    title: '',
    title_custom: '',
    weight: '',
    temperature: '',
    medication: '',
    notes: ''
  });

  // Dynamic attachments (title + url)
  const [attachments, setAttachments] = useState([{ title: '', url: '' }]);

  // Reminder fields
  const [reminderDate, setReminderDate] = useState('');
  const [reminderMessage, setReminderMessage] = useState('');
  const [reminderRecurrence, setReminderRecurrence] = useState('none');

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const toastTimeoutRef = useRef(null);

  useEffect(() => {
    checkUser();
  }, []);

  useEffect(() => {
    if (clinic) {
      fetchClients();
      fetchPets();
    }
  }, [clinic]);

  useEffect(() => {
    if (user && clinic) {
      fetchCurrentVet();
    }
  }, [user, clinic]);

  useEffect(() => {
    if (petId && pets.length > 0 && clients.length > 0) {
      const pet = pets.find(p => p.id === petId);
      if (pet) {
        setSelectedPet(pet);
        const client = clients.find(c => c.id === pet.owner_id);
        setSelectedClient(client);
      }
    }
  }, [petId, pets, clients]);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  // Warehouse integration handlers
  const handleWarehouseRemove = async (itemsToRemove) => {
    try {
      // Store the changes for potential rollback (don't actually remove yet)
      setPendingWarehouseChanges(itemsToRemove);
      
      // Auto-fill medication field with removed items
      const medicationText = itemsToRemove.map(item => 
        `${item.name}: ${item.quantityToRemove} ${item.unit}`
      ).join('\n');
      
      setMedicalRecordForm(prev => ({
        ...prev,
        medication: prev.medication ? `${prev.medication}\n\n${medicationText}` : medicationText
      }));
      
      showToast('Προϊόντα προστέθηκαν στα φάρμακα (θα αφαιρεθούν όταν αποθηκευτεί η εγγραφή)', 'success');
    } catch (error) {
      console.error('Error processing warehouse items:', error);
      showToast('Σφάλμα επεξεργασίας προϊόντων', 'error');
    }
  };

  const handleWarehouseDialogClose = () => {
    setShowWarehouseDialog(false);
  };

  const handleCancel = () => {
    // Clear pending warehouse changes (no rollback needed since we didn't actually remove anything)
    setPendingWarehouseChanges([]);
    navigate(-1);
  };

  const checkUser = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('❌ Error getting user:', userError);
        navigate('/login');
        return;
      }
      
      if (!user) {
        if (import.meta.env.DEV) console.log('❌ No user found, redirecting to login');
        navigate('/login');
        return;
      }

      setUser(user);

      // Fetch clinic data (supports both owners and vet employees)
      const { clinic: clinicData, error: clinicResolveError } = await resolveClinicForUser(supabase, user.id);
      if (clinicResolveError) {
        console.error('Error resolving clinic:', clinicResolveError);
      } else {
        setClinic(clinicData);
      }
    } catch (error) {
      console.error('❌ Unexpected error in checkUser:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('owners')
        .select('*')
        .eq('clinic_id', clinic.id)
        .is('deleted_at', null)
        .order('first_name');

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      showToast('Σφάλμα κατά τη φόρτωση των πελατών.', 'error');
    }
  };

  const fetchPets = async () => {
    try {
      // Get pets that belong to owners in this clinic using server-side filter
      const { data, error } = await supabase
        .from('pets')
        .select(`
          *,
          owners:owner_id!inner(
            id,
            first_name,
            last_name,
            clinic_id
          )
        `)
        .eq('owners.clinic_id', clinic.id)
        .is('deleted_at', null)
        .order('name');

      if (error) throw error;

      setPets(data || []);
    } catch (error) {
      console.error('Error fetching pets:', error);
      showToast('Σφάλμα κατά τη φόρτωση των κατοικιδίου.', 'error');
    }
  };

  const fetchCurrentVet = async () => {
    try {
      const { data, error } = await supabase
        .from('vets')
        .select('id, first_name, last_name')
        .eq('user_id', user.id)
        .eq('clinic_id', clinic.id)
        .single();

      if (!error && data) {
        setCurrentVet(data);
      } else {
        setCurrentVet(null);
      }
    } catch (err) {
      console.warn('Could not resolve current vet for user:', err);
      setCurrentVet(null);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setMedicalRecordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAttachmentChange = (index, field, value) => {
    setAttachments(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addAttachmentRow = () => {
    setAttachments(prev => [...prev, { title: '', url: '' }]);
  };

  const removeAttachmentRow = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const isValidHttpUrl = (value) => {
    if (!value) return false;
    try {
      const u = new URL(value);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch (_) {
      return false;
    }
  };

  const getPetFriendlyName = () => {
    if (selectedPet?.name) return selectedPet.name;
    const fallback = pets.find(p => p.id === medicalRecordForm.pet_id);
    return fallback?.name || 'το κατοικίδιό σας';
  };

  const applyReminderTemplate = (template) => {
    const petName = getPetFriendlyName();
    let message = '';
    if (template === 'monthly_pills') {
      message = `Ήρθε η ώρα για τα μηνιαία χάπια του ${petName}.`;
    } else if (template === 'vaccination') {
      message = `Ήρθε η ώρα για τον εμβολιασμό του ${petName}. Παρακαλώ κλείστε ραντεβού στο παρακάτω σύνδεσμο: ...`;
    } else if (template === 'recheck') {
      message = `Ήρθε η ώρα για τον επανέλεγχο του ${petName}. Παρακαλώ κλείστε ραντεβού για επανεξέταση.`;
    }
    setReminderMessage(message);
  };

  const createMedicalRecord = async () => {
    // Use local variables to avoid race conditions with async state updates
    let petToUse = selectedPet;
    let clientToUse = selectedClient;

    // Validate required fields based on whether pet is pre-selected
    if (petId) {
      // When pet is pre-selected, ensure we have the data
      if (!selectedClient || !selectedPet) {
        showToast('Σφάλμα: Δεν μπορούν να βρεθούν τα στοιχεία του πελάτη ή του κατοικιδίου.', 'error');
        return;
      }
    } else {
      // When no pet is pre-selected, ensure user has selected client and pet
      if (!medicalRecordForm.client_id || !medicalRecordForm.pet_id) {
        showToast('Παρακαλώ επιλέξτε πελάτη και κατοικίδιο.', 'error');
        return;
      }
      // Get pet and client from form values using local variables
      const pet = pets.find(p => p.id === medicalRecordForm.pet_id);
      const client = clients.find(c => c.id === medicalRecordForm.client_id);
      if (pet && client) {
        petToUse = pet;
        clientToUse = client;
        setSelectedPet(pet);
        setSelectedClient(client);
      } else {
        showToast('Σφάλμα: Δεν μπορούν να βρεθούν τα στοιχεία του πελάτη ή του κατοικιδίου.', 'error');
        return;
      }
    }

    if (!medicalRecordForm.date || !medicalRecordForm.title || 
        (medicalRecordForm.title === 'other' && !medicalRecordForm.title_custom.trim()) || 
        !medicalRecordForm.notes.trim()) {
      showToast('Παρακαλώ συμπληρώστε όλα τα υποχρεωτικά πεδία.', 'error');
      return;
    }

    setSubmitting(true);

    try {
      const finalTitle = medicalRecordForm.title === 'other' ? medicalRecordForm.title_custom.trim() : medicalRecordForm.title;
      
      const { data: insertedRecord, error } = await supabase
        .from('medical_records')
        .insert({
          pet_id: petToUse.id,
          vet_id: currentVet?.id || null,
          title: finalTitle,
          notes: medicalRecordForm.notes.trim(),
          medication: medicalRecordForm.medication.trim() || null,
          weight: medicalRecordForm.weight ? parseFloat(medicalRecordForm.weight) : null,
          temperature: medicalRecordForm.temperature ? parseFloat(medicalRecordForm.temperature) : null,
          visit_date: medicalRecordForm.date || new Date().toISOString().split('T')[0],
          attachments: (() => {
            const list = attachments
              .map(a => ({ title: (a.title || '').trim() || null, url: (a.url || '').trim() }))
              .filter(a => a.url.length > 0);
            return list.length > 0 ? list : [];
          })(),
          diagnosis: null,
          treatment: null,
          created_at: new Date().toISOString()
        })
        .select('id')
        .single();

      if (error) throw error;

      // Create reminder if provided
      if (reminderDate && reminderMessage.trim()) {
        const recipientEmail = clientToUse?.email || null;
        const { error: reminderError } = await supabase
          .from('reminders')
          .insert({
            clinic_id: clinic.id,
            owner_id: clientToUse?.id,
            pet_id: petToUse?.id,
            medical_record_id: insertedRecord?.id || null,
            reminder_date: new Date(reminderDate).toISOString(),
            message: reminderMessage.trim(),
            recipient_email: recipientEmail,
            recurrence: reminderRecurrence,
          });
        if (reminderError) {
          console.error('Reminder creation failed:', reminderError);
          showToast('Η εγγραφή αποθηκεύτηκε αλλά η υπενθύμιση απέτυχε.', 'error');
        }
      }

      // Remove warehouse items after successful record creation
      if (pendingWarehouseChanges.length > 0) {
        try {
          const result = await removeItems(pendingWarehouseChanges);
          if (result.success) {
            if (import.meta.env.DEV) console.log('Warehouse items removed successfully');
          } else {
            console.warn('Failed to remove warehouse items:', result.message);
            showToast('Προσοχή: Η αφαίρεση αποθέματος απέτυχε.', 'error');
          }
        } catch (warehouseError) {
          console.error('Error removing warehouse items:', warehouseError);
          showToast('Προσοχή: Η αφαίρεση αποθέματος απέτυχε.', 'error');
          // Don't fail the entire operation if warehouse removal fails
        }
      }

      showToast(`Η ιατρική εγγραφή δημιουργήθηκε επιτυχώς για το κατοικίδιο "${petToUse?.name}"!`, 'success');
      
      // Navigate back to clients page after a short delay
      setTimeout(() => {
        navigate('/clients');
      }, 2000);
      
    } catch (error) {
      console.error('Error creating medical record:', error);
      showToast('Σφάλμα κατά τη δημιουργία της ιατρικής εγγραφής. Παρακαλώ δοκιμάστε ξανά.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />
      {/* Shared Sidebar */}
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8">
        {/* Toast Notification */}
        {toast.show && (
          <div className="fixed top-4 right-4 left-4 sm:left-auto z-50">
            <div
              className="flex items-center px-5 py-4 rounded-xl border max-w-sm ml-auto transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
                backdropFilter: 'blur(10px)',
                borderColor: toast.type === 'success' ? '#10B981' : toast.type === 'error' ? '#EF4444' : '#3B82F6',
                boxShadow: `0 10px 25px rgba(0,0,0,0.3), 0 0 20px ${
                  toast.type === 'success' ? 'rgba(16,185,129,0.2)' :
                  toast.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)'
                }`
              }}
            >
              <div className="flex-shrink-0 mr-3">
                {toast.type === 'success' && (
                  <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                )}
                {toast.type === 'error' && (
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                )}
                {toast.type === 'info' && (
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                )}
              </div>
              <div className="text-white font-medium flex-1">{toast.message}</div>
              <button
                onClick={() => setToast({ show: false, message: '', type: 'success' })}
                className="ml-4 text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Νέα Ιατρική Εγγραφή</h1>
              <p className="text-gray-400 text-base lg:text-lg">
                Δημιουργία νέας ιατρικής εγγραφής για κατοικίδιο
              </p>
            </div>
            <button
              onClick={() => navigate('/clients')}
              className="px-5 py-2.5 rounded-lg font-medium text-gray-300 transition-all duration-200 hover:bg-gray-600"
              style={{ backgroundColor: '#4A5568' }}
            >
              ← Πίσω
            </button>
          </div>
        </div>

        {/* Medical Record Form */}
        <div className="max-w-4xl">
          <div className="rounded-xl overflow-hidden border transition-all duration-300 hover:border-emerald-500/30" style={{ background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)', borderColor: '#374151', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
            <div className="px-4 lg:px-6 py-4 border-b" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(55,65,81,0.8), rgba(31,41,55,0.8))' }}>
              <h3 className="text-lg lg:text-xl font-semibold text-white">Στοιχεία Ιατρικής Εγγραφής</h3>
            </div>
            
            <div className="p-4 lg:p-6">
              <form onSubmit={(e) => { e.preventDefault(); createMedicalRecord(); }} className="space-y-6">
                {/* Client and Pet Selection/Information */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Client Selection/Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Πελάτης *
                    </label>
                    {petId ? (
                      // Read-only when pet is pre-selected
                      <div className="w-full px-3 py-2 rounded-lg border text-white bg-gray-600 border-gray-500 cursor-not-allowed">
                        {selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Φόρτωση...'}
                      </div>
                    ) : (
                      // Selectable when no pet is pre-selected
                      <select
                        name="client_id"
                        value={medicalRecordForm.client_id}
                        onChange={(e) => {
                          const clientId = e.target.value;
                          setMedicalRecordForm(prev => ({
                            ...prev,
                            client_id: clientId,
                            pet_id: '' // Reset pet selection when client changes
                          }));
                          setSelectedClient(clients.find(c => c.id === clientId));
                          setSelectedPet(null);
                        }}
                        required
                        className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                        style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                      >
                        <option value="">Επιλέξτε πελάτη</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.first_name} {client.last_name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Pet Selection/Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Κατοικίδιο *
                    </label>
                    {petId ? (
                      // Read-only when pet is pre-selected
                      <div className="w-full px-3 py-2 rounded-lg border text-white bg-gray-600 border-gray-500 cursor-not-allowed">
                        {selectedPet ? `${selectedPet.name} (${selectedPet.species})` : 'Φόρτωση...'}
                      </div>
                    ) : (
                      // Selectable when no pet is pre-selected
                      <select
                        name="pet_id"
                        value={medicalRecordForm.pet_id}
                        onChange={(e) => {
                          const petId = e.target.value;
                          const pet = pets.find(p => p.id === petId);
                          if (pet) {
                            setSelectedPet(pet);
                            setSelectedClient(clients.find(c => c.id === pet.owner_id));
                            setMedicalRecordForm(prev => ({
                              ...prev,
                              pet_id: petId,
                              client_id: pet.owner_id
                            }));
                          }
                        }}
                        required
                        disabled={!medicalRecordForm.client_id}
                        className="w-full px-3 py-2 rounded-lg border text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                        style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                      >
                        <option value="">Επιλέξτε κατοικίδιο</option>
                        {pets
                          .filter(pet => !medicalRecordForm.client_id || pet.owner_id === medicalRecordForm.client_id)
                          .map((pet) => (
                            <option key={pet.id} value={pet.id}>
                              {pet.name} ({pet.species})
                            </option>
                          ))}
                      </select>
                    )}
                  </div>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Ημερομηνία Επισκέψεως *
                  </label>
                  <DatePicker
                    selected={medicalRecordForm.date ? new Date(medicalRecordForm.date + 'T00:00:00') : null}
                    onChange={(date) => {
                      if (date) {
                        const yyyy = date.getFullYear();
                        const mm = String(date.getMonth() + 1).padStart(2, '0');
                        const dd = String(date.getDate()).padStart(2, '0');
                        handleFormChange({ target: { name: 'date', value: `${yyyy}-${mm}-${dd}` } });
                      } else {
                        handleFormChange({ target: { name: 'date', value: '' } });
                      }
                    }}
                    maxDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    scrollableYearDropdown
                    yearDropdownItemNumber={40}
                    placeholderText="Επιλέξτε ημερομηνία"
                    className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                    calendarClassName="dark-datepicker"
                    wrapperClassName="w-full"
                  />
                </div>

                {/* Title Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Title Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Τύπος Επισκέψεως *
                    </label>
                    <select
                      name="title"
                      value={medicalRecordForm.title}
                      onChange={handleFormChange}
                      required
                      className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                      style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                    >
                      <option value="">Επιλέξτε τύπο</option>
                      <option value="check up">Γενικός έλεγχος</option>
                      <option value="vaccination">Εμβολιασμός</option>
                      <option value="surgery">Χειρουργείο</option>
                      <option value="emergency">Επείγον</option>
                      <option value="other">Άλλο</option>
                    </select>
                  </div>

                  {/* Custom Title - Only show when "other" is selected */}
                  {medicalRecordForm.title === 'other' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Προσαρμοσμένος Τύπος *
                      </label>
                      <input
                        type="text"
                        name="title_custom"
                        value={medicalRecordForm.title_custom}
                        onChange={handleFormChange}
                        required
                        className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                        style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                        placeholder="Εισάγετε τον τύπο"
                      />
                    </div>
                  )}
                </div>

                {/* Weight and Temperature - Side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Weight */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Βάρος (kg)
                    </label>
                    <input
                      type="number"
                      name="weight"
                      value={medicalRecordForm.weight}
                      onChange={handleFormChange}
                      step="0.1"
                      min="0"
                      className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                      style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                      placeholder="0.0"
                    />
                  </div>

                  {/* Temperature */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Θερμοκρασία (°C)
                    </label>
                    <input
                      type="number"
                      name="temperature"
                      value={medicalRecordForm.temperature}
                      onChange={handleFormChange}
                      step="0.1"
                      min="35"
                      max="42"
                      className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                      style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                      placeholder="36.5"
                    />
                  </div>
                </div>

                {/* Medication */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-300">
                      Φάρμακα
                    </label>
                    {warehouseItems && warehouseItems.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowWarehouseDialog(true)}
                        className="flex items-center px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-md transition-colors duration-200 group"
                        title="Προσθήκη προϊόντων από την αποθήκη"
                      >
                        <span className="group-hover:hidden">+</span>
                        <span className="hidden group-hover:inline">Προσθήκη από αποθήκη</span>
                      </button>
                    )}
                  </div>
                  <textarea
                    name="medication"
                    value={medicalRecordForm.medication}
                    onChange={handleFormChange}
                    rows="3"
                    className="w-full px-3 py-2 rounded-lg border text-white resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                    style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                    placeholder="Φάρμακα που χορηγήθηκαν..."
                  />
                </div>

                {/* Attachments URLs (title + url rows) */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Συννημμένα
                  </label>
                  <div className="space-y-3">
                    {attachments.map((att, idx) => (
                      <div key={idx} className="grid grid-cols-1 lg:grid-cols-[1fr_2fr_auto] gap-3 items-center">
                        <input
                          type="text"
                          value={att.title}
                          onChange={(e) => handleAttachmentChange(idx, 'title', e.target.value)}
                          placeholder="Τίτλος αρχείου (προαιρετικό)"
                          className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                          style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                        />
                        <div>
                          <input
                            type="url"
                            value={att.url}
                            onChange={(e) => handleAttachmentChange(idx, 'url', e.target.value)}
                            placeholder="https://cloud.example.com/file.pdf ή .jpg/.png"
                            className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                            style={{ backgroundColor: '#1F2937', borderColor: att.url && !isValidHttpUrl(att.url) ? '#EF4444' : '#374151' }}
                          />
                          {att.url && !isValidHttpUrl(att.url) && (
                            <p className="mt-1 text-xs" style={{ color: '#FCA5A5' }}>Παρακαλώ εισάγετε έγκυρο URL που ξεκινά με http/https.</p>
                          )}
                        </div>
                        <div className="flex gap-2 justify-end items-center">
                          <a
                            href={isValidHttpUrl(att.url) ? att.url : undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`px-3 py-2 rounded-lg text-white text-xs transition-transform duration-200 ${isValidHttpUrl(att.url) ? 'hover:scale-[1.02]' : 'opacity-50 pointer-events-none'}`}
                            style={{ backgroundColor: '#3B82F6' }}
                          >
                            Προεπισκόπηση
                          </a>
                          {attachments.length > 1 && (
                            <button type="button" onClick={() => removeAttachmentRow(idx)} className="px-3 py-2 rounded-lg text-white transition-all duration-200 hover:opacity-90" style={{ backgroundColor: '#EF4444' }}>✕</button>
                          )}
                          {idx === attachments.length - 1 && (
                            <button type="button" onClick={addAttachmentRow} className="px-3 py-2 rounded-lg text-white transition-transform duration-200 hover:scale-[1.02]" style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}>＋</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-400">Μπορείτε να προσθέσετε πολλαπλά αρχεία. Τα URLs θα εμφανιστούν στο ιστορικό.</p>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Σημειώσεις *
                  </label>
                  <textarea
                    name="notes"
                    value={medicalRecordForm.notes}
                    onChange={handleFormChange}
                    required
                    rows="4"
                    className="w-full px-3 py-2 rounded-lg border text-white resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                    style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                    placeholder="Σημειώσεις από τον κτηνίατρο, διάγνωση, θεραπεία..."
                  />
                </div>

                {/* Υπενθύμιση */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">Υπενθύμιση</label>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-2">Ημερομηνία</label>
                      <DatePicker
                        selected={reminderDate ? new Date(reminderDate + 'T00:00:00') : null}
                        onChange={(date) => {
                          if (date) {
                            const yyyy = date.getFullYear();
                            const mm = String(date.getMonth() + 1).padStart(2, '0');
                            const dd = String(date.getDate()).padStart(2, '0');
                            setReminderDate(`${yyyy}-${mm}-${dd}`);
                          } else {
                            setReminderDate('');
                          }
                        }}
                        dateFormat="dd/MM/yyyy"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        isClearable
                        placeholderText="Επιλέξτε ημερομηνία"
                        className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                        calendarClassName="dark-datepicker"
                        wrapperClassName="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-2">Επανάληψη</label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <select
                          value={reminderRecurrence}
                          onChange={(e) => setReminderRecurrence(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                          style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                        >
                          <option value="none">Καμία</option>
                          <option value="monthly">Μηνιαία</option>
                          <option value="yearly">Ετήσια</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <button
                          type="button"
                          onClick={() => applyReminderTemplate('monthly_pills')}
                          className="px-3 py-2 rounded-lg text-white text-xs transition-transform duration-200 hover:scale-[1.02]"
                          style={{ backgroundColor: '#10B981' }}
                          title="Μηνιαία χάπια"
                        >
                          Μηνιαία χάπια
                        </button>
                        <button
                          type="button"
                          onClick={() => applyReminderTemplate('vaccination')}
                          className="px-3 py-2 rounded-lg text-white text-xs transition-transform duration-200 hover:scale-[1.02]"
                          style={{ backgroundColor: '#3B82F6' }}
                          title="Εμβολιασμός"
                        >
                          Εμβολιασμός
                        </button>
                        <button
                          type="button"
                          onClick={() => applyReminderTemplate('recheck')}
                          className="px-3 py-2 rounded-lg text-white text-xs transition-transform duration-200 hover:scale-[1.02]"
                          style={{ backgroundColor: '#F59E0B' }}
                          title="Επανέλεγχος"
                        >
                          Επανέλεγχος
                        </button>
                      </div>
                      <label className="block text-xs font-medium text-gray-400 mb-2">Μήνυμα</label>
                      <textarea
                        value={reminderMessage}
                        onChange={(e) => setReminderMessage(e.target.value)}
                        rows="4"
                        placeholder="Π.χ. Εμβολιασμός επανελέγχου σε 6 μήνες"
                        className="w-full px-3 py-2 rounded-lg border text-white resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/30"
                        style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex flex-col lg:flex-row justify-end space-y-3 lg:space-y-0 lg:space-x-3 pt-6">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="w-full lg:w-auto px-6 py-3 rounded-lg font-medium text-gray-300 transition-all duration-200 hover:bg-gray-600"
                    style={{ backgroundColor: '#4A5568' }}
                  >
                    Ακύρωση
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full lg:w-auto px-6 py-3 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50"
                    style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
                  >
                    {submitting ? 'Δημιουργία...' : 'Δημιουργία Εγγραφής'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Warehouse Remove Items Dialog */}
      <RemoveItemsDialog 
        isOpen={showWarehouseDialog} 
        onClose={handleWarehouseDialogClose}
        onRemove={handleWarehouseRemove}
      />
    </div>
  );
};

export default NewMedicalRecordPage;
